import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useNavigation } from '@zet/common/hooks/useNavigation'
// Functions specific to this module
import { useExperiments } from '@zet/experiments'

import { useChatStore } from '../../../store/chatStore'
import { getComponentKey } from '../../utils/helperFunction'
import { ChatActions } from '../../utils/interface'
import {
  createChat,
  createTicket,
  getChatHistory,
  getFeedbackFormLink,
  getLeadOptions,
  getLeads,
  getTicketCount,
  getTicketList,
  reopenTicket,
  sendRatings,
} from '../functions'
import {
  ChatHistoryRequestData,
  ChatReply,
  ITicketListRequestData,
  LeadOptions,
  LeadsRequestData, // Type for leads request data
} from '../types'

// Types related to this module

// Define a custom hook for fetching a ticket ID
const useTicketID = (leadId?: number) => {
  return useQuery(['ticket', leadId], () => createTicket(leadId), {
    enabled: false, // Disable the query initially
  })
}

// Define a custom hook for handling mutations
const useHandleMutation = () => {
  const queryClient = useQueryClient()
  const { replace } = useNavigation()

  const setConversation = useChatStore((state) => state.setConversation)
  const updateState: ChatActions[] = [
    {
      type: 'setLoading',
      payload: false,
    },
  ]

  const handleSuccess = (data: ChatReply) => {
    // Invalidate queries to trigger updates
    queryClient.invalidateQueries({
      queryKey: ['ticketCount'],
    })
    queryClient.invalidateQueries({
      queryKey: ['ticketList'],
    })

    if (data?.message_type === 'general_query_chat') {
      replace('customerChatRedirect')
      return data
    } else if (data?.message_type === 'ticket_redirected') {
      replace('redirectToExistingTicket', { id: data?.id })
      return data
    } else {
      if (data?.user_options) {
        switch (data?.user_options?.type) {
          case 'ticket_creation_event':
            // Handle a specific type of chat reply it Ticket Creation Event
            updateState.push({
              type: 'push',
              payload: {
                message: data.message,
                createdOn: data.created_on,
                sender: 'ZET',
                componentKey: getComponentKey('message'),
              },
            })
            updateState.push({
              type: 'push',
              payload: {
                userOptions: data.user_options,
                subject: data?.subject,
                ticketCreationId: data?.id?.toString?.(),
                sender: 'ZET',
                componentKey: getComponentKey(data.user_options.type),
              },
            })
            setConversation(updateState)
            return data
          case 'share_link':
            // Handle a specific type of chat reply it Ticket Creation Event
            updateState.push({
              type: 'push',
              payload: {
                message: data?.message,
                createdOn: data?.created_on,
                sender: 'ZET',
                componentKey: getComponentKey('message'),
              },
            })
            updateState.push({
              type: 'push',
              payload: {
                userOptions: data?.user_options,
                sender: 'ZET',
                chat_meta_data: data?.chat_meta_data,
                componentKey: getComponentKey(data?.user_options.type),
              },
            })
            setConversation(updateState)
            return data
          case 'reply_on_this':
            // Handle another type of chat reply it Reply button Scenarios
            updateState.push({
              type: 'push',
              payload: {
                userOptions: data.user_options,
                subject: data?.message,
                sender: 'ZET',
                componentKey: getComponentKey(data.user_options.type),
                createdOn: data.created_on,
              },
            })
            setConversation(updateState)
            return data
          default:
            // Handle other cases
            updateState.push({
              type: 'push',
              payload: {
                message: data.message,
                createdOn: data.created_on,
                sender: 'ZET',
                componentKey: getComponentKey('message'),
              },
            })
            updateState.push({
              type: 'push',
              payload: {
                userOptions: data.user_options,
                sender: 'ZET',
                componentKey: getComponentKey(data.user_options.type),
              },
            })
        }
      } else {
        data?.message &&
          updateState.push({
            type: 'push',
            payload: {
              message: data.message,
              createdOn: data.created_on,
              sender: 'ZET',
              componentKey: getComponentKey('message'),
            },
          })
      }
      setConversation(updateState)
      return data
    }
  }

  const handleError = () => {
    setConversation({ type: 'setLoading', payload: false })
  }

  return { handleSuccess, handleError }
}

// Define a custom hook for handling chat replies
const useChatReply = () => {
  const { handleError, handleSuccess } = useHandleMutation()
  return useMutation(createChat, {
    onSuccess: handleSuccess,
    onError: handleError,
    retry: false,
  })
}

const useSendRating = () => {
  return useMutation(sendRatings)
}

// Define a custom hook for fetching the ticket count
const useTicketCount = () => {
  const { data } = useExperiments()
  return useQuery(['ticketCount'], getTicketCount, {
    enabled: !!data?.UI_isRaiseComplaint,
  })
}

// Define a custom hook for fetching the top ticket list
const useTopTicketList = ({
  limit,
  status,
  leadId,
}: ITicketListRequestData) => {
  return useInfiniteQuery(['ticketList', status, limit], () =>
    getTicketList({
      offset: 0,
      limit,
      leadId,
      status,
    })
  )
}

// Define a custom hook for fetching a ticket list
const useTicketList = ({
  limit = 10,
  status = 'IN_PROGRESS',
  customerName,
  leadId,
}: ITicketListRequestData) => {
  return useInfiniteQuery(
    ['ticketList', status, customerName, leadId],
    ({ pageParam = 0 }) =>
      getTicketList({
        offset: pageParam,
        limit,
        status,
        customerName,
        leadId,
      }),
    {
      getNextPageParam: (lastPage, pages) => {
        if (pages.length * 10 <= lastPage.count) {
          return lastPage.nextOffset
        }
        return undefined
      },
    }
  )
}

// Define a custom hook for fetching chat history
const useChatHistory = ({ limit, ticketID }: ChatHistoryRequestData) => {
  return useInfiniteQuery(
    ['chatHistory', ticketID],
    ({ pageParam = 0 }) =>
      getChatHistory({
        ticketID,
        offset: pageParam,
        limit,
      }),
    {
      getNextPageParam: (lastPage, pages) => {
        if (pages.length * 10 <= lastPage.total) {
          return lastPage.nextOffset
        }
        return undefined
      },
    }
  )
}

// Define a custom hook for fetching lead options
const useLeadOptions = ({ ticketId }: LeadOptions) => {
  return useQuery(['options'], () => getLeadOptions({ ticketId }))
}

// Define a custom hook for fetching a list of leads
const useLeadsList = ({
  limit = 10,
  status,
  customerName,
  leadId,
}: LeadsRequestData) => {
  return useInfiniteQuery(
    ['agentTeam', { customerName, leadId, status }],
    ({ pageParam = 0 }) => {
      return getLeads({
        offset: pageParam,
        limit,
        status,
        customerName,
        leadId,
      })
    },
    {
      getNextPageParam: (lastPage, pages) => {
        if (pages.length * 10 <= lastPage.total) {
          return lastPage.nextOffset
        }
        return undefined
      },
    }
  )
}

const useReopenTicket = () => {
  const { refetch } = useTopTicketList({ limit: 2 })
  const queryClient = useQueryClient()
  return useMutation(reopenTicket, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ticketCount'],
      })
      queryClient.invalidateQueries({
        queryKey: ['ticketList'],
      })
      refetch()
    },
  })
}

const useGetFeedbackFormLink = () => {
  return useQuery(['feedbackForm'], getFeedbackFormLink)
}
// Export the custom hooks for use in other parts of the application
export {
  useChatHistory,
  useChatReply,
  useGetFeedbackFormLink,
  useLeadOptions,
  useLeadsList,
  useReopenTicket,
  useSendRating,
  useTicketCount,
  useTicketID,
  useTicketList,
  useTopTicketList,
}
